import { useEffect, useState } from 'react';
import { TEAM_FACTION_NAMES, getDisplayTeamBg } from '../displayTheme';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface BondOption {
  id: string;
  text: string;
  resolvedText?: string;
}

interface BondVoteDisplayProps {
  voteId: string;
  activeTeam?: TeamColor;
}

function BondVoteDisplay({ voteId, activeTeam }: BondVoteDisplayProps) {
  const [options, setOptions] = useState<BondOption[]>([]);
  const [fromTeam, setFromTeam] = useState<TeamColor | null>(null);
  const [heroName, setHeroName] = useState<string>('');

  // Fetch bond vote data
  useEffect(() => {
    const fetchBondVote = async () => {
      try {
        // Fetch vote details
        const response = await fetch(`/api/bond-votes/${voteId}`);
        if (response.ok) {
          const data = await response.json();
          setFromTeam(data.bondVote.fromTeam);
          setOptions(data.bondVote.options || []);
        }

        // Fetch resolved options with hero names
        const resultsResponse = await fetch(`/api/bond-votes/${voteId}/results`);
        if (resultsResponse.ok) {
          const resultsData = await resultsResponse.json();
          setOptions(resultsData.options || []);
        }
      } catch (error) {
        console.error('[BondVoteDisplay] Error fetching bond vote:', error);
      }
    };

    fetchBondVote();
  }, [voteId]);

  // Fetch hero name for the team
  useEffect(() => {
    const fetchHeroName = async () => {
      if (!fromTeam) return;

      try {
        // We need to get the session ID from the vote to fetch characters
        const response = await fetch(`/api/bond-votes/${voteId}`);
        if (response.ok) {
          const data = await response.json();
          const sessionId = data.bondVote.sessionId;

          // Get the winning sheet from sheet votes
          const votesResponse = await fetch(`/api/sheet-votes/session/${sessionId}/latest`);
          if (votesResponse.ok) {
            const votesData = await votesResponse.json();
            const teamVote = votesData.votes?.find((v: any) => v.teamColor === fromTeam && v.finalSheetId);
            if (teamVote?.winnerSheet?.name) {
              setHeroName(teamVote.winnerSheet.name);
            }
          }
        }
      } catch (error) {
        console.error('[BondVoteDisplay] Error fetching hero:', error);
      }
    };

    fetchHeroName();
  }, [voteId, fromTeam]);

  const team = activeTeam || fromTeam;
  const bgClass = getDisplayTeamBg(team);

  return (
    <div className={`h-screen w-screen flex flex-col items-center justify-center p-12 ${bgClass}`}>
      {/* Titolo */}
      <div className="mb-12 text-center">
        <h1 className="text-6xl font-bold text-parchment-100 font-display mb-4">Scegli il Legame</h1>
        {heroName && (
          <p className="text-4xl text-parchment-200 font-heading">
            {heroName}
          </p>
        )}
        {team && (
          <p className="text-2xl text-parchment-400 font-heading mt-2">
            Squadra {TEAM_FACTION_NAMES[team]} sta scegliendo
          </p>
        )}
      </div>

      {/* Opzioni */}
      <div className="w-full max-w-4xl space-y-6">
        {options.map((option, index) => (
          <div
            key={option.id}
            className="display-panel p-8"
          >
            <div className="flex items-center gap-6">
              <span className="text-5xl font-bold text-parchment-400 font-heading">{index + 1}</span>
              <p className="text-4xl font-semibold text-parchment-100 font-heading">
                {option.resolvedText || option.text}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BondVoteDisplay;
